import React from 'react';
import './SubmissionsPage.less';

const SectionHeading = ({ level, id, children }: { level: 'h2' | 'h3'; id: string; children: React.ReactNode }) => {
  const Tag = level;
  return (
    <Tag id={id} className="anchorHeading">
      {children}
      <a className="anchorLink" href={`#${id}`} aria-label="Link to section">#</a>
    </Tag>
  );
};

const SubmissionsPage = () => {
  return (
    <div className="submissionsPage">
        <SectionHeading level="h2" id="morphology-submissions">Morphology Submissions</SectionHeading>

        <p>Note: We are no longer taking new submissions for Morphology. Read more <a href="/on-ending-morphology">here</a>.</p>

        <p>
            Please send any Morphology submissions via email to <a target="_blank" href="mailto:submissions@dailybaffle.com">submissions@dailybaffle.com</a>. You can use the <a href="/formatter">formatter tool</a> and choose "Output with Solutions" to format your puzzle nicely for the email. Please include your desired byline and a payment method, such as Venmo, Cashapp, or Paypal. The rate is $100 per puzzle, paid upon acceptance and after any suggested revisions. We will never make any changes to your puzzle without your full approval.
        </p>

        <p>
            We try to respond to all submissions, but it might take some time. Please do not send in more than two puzzles at a time until you receive a response.
        </p>

        <SectionHeading level="h2" id="assistive-tools">Assistive Tools</SectionHeading>

        <p>
            Here is a simple wordfinding tool that will find all words within a specified morph distance of a given word:
            <br/>
            <a target="_blank" href="/wordfinder">Wordfinder</a>
        </p>
        <p>
            And here is a formatter that will help with sharing your puzzle data by neatly laying it out:
            <br/>
            <a href="/formatter">Formatter</a>
        </p>

        <SectionHeading level="h2" id="construction-guidelines">Construction Guidelines</SectionHeading>

        <SectionHeading level="h3" id="basics">Basics</SectionHeading>

        <p>
            Aim to keep the puzzle to around 15 clues, give or take, because <span className="italicText">The Daily Baffle</span> leans more towards bite-size puzzles like Wordle or the NYT mini crossword rather than larger commitment puzzles like the NYT daily crossword. But don’t hesitate to make your puzzle a few clues longer if it leads to a better sequence and a better puzzle. Puzzles should not be shorter than 13 entries unless the theme is unbelievably well-executed and adding additional clues would only clutter the perfection.
        </p>

        <p>
            Since it's only around 15 clues and authors don't have to worry about intersecting the words, these generally take much less time to build than a crossword.
        </p>

        <p>
            A theme is optional, but the first and last clues of a Morphology puzzle inherently have less information than the clues in the middle since they only have one adjacent word instead of two. So if it’s possible to link them to each other somehow, this can make for a better solving experience. But this isn't mandatory, so don't be afraid to send in puzzles with unrelated starts and finishes.
        </p>

        <SectionHeading level="h3" id="morph-sequencing">Morph Sequencing</SectionHeading>

        <p>
            For each clue, the net morph distance (the sum of X+Y+Z) should be chosen carefully, and the difficulty of a morph should be balanced with the difficulty of its accompanying semantic clue.
        </p>

        <p>
            This is very dependent on word length. Consider the following example:
        </p>

        <div className="example">
          <pre>A vessel for navigating the sea               B O A T</pre>
          <pre>Adjust to new conditions (+1, ~2)             _ _ _ _ _</pre>
        </div>

        <p>
            In this context there’s 3 changes happening to a word that’s only 4 letters long, so the morph here is very unhelpful and the solver is almost completely dependent on the semantic clue to arrive at the solution ADAPT.
        </p>

        <p>
            Consider this counterexample:
        </p>

        <div className="example">
          <pre>Add more detail                     E L A B O R A T E</pre>
          <pre>Consider carefully (+1, ~2)         _ _ _ _ _ _ _ _ _ _</pre>
        </div>

        <p>
            In this case, that same morph of (+1, ~2) is only 1/3 the length of the much longer ELABORATE, and is thus much more helpful to reach the solution of DELIBERATE.
        </p>

        <p>
            With that in mind, three-part morphs like (+1, -1, ~2) are best to be avoided entirely, as it’s nearly impossible to reason around them, although the simple (+1, -1, ~1) can occasionally get by. Likewise, single-part morphs can often work with somewhat higher values, such as (-5), since they’re much easier to reason around. (Although ~5 would be pretty rough.)
        </p>

        <p>
            In general, it’s best to keep the average net morph of your puzzle somewhere around 2 and rarely go above a net morph of 3 for any clue.
        </p>

        <SectionHeading level="h3" id="island-letters">Island Letters</SectionHeading>

        <p>
            One other thing to look out for is what might be termed <span className="italicText">island letters</span>. Don’t worry about this for the first and last clues, but for the rest, an island letter is any letter in a solution word that doesn’t appear in either of the adjacent solutions above or below. The adjacent words therefore can’t help a solver to deduce that letter’s identity.
        </p>

        <p>
            An example: CLOSE – CHOSE – CHORE – CHOKE – CHOCK – CHECK.
        </p>

        <p>
            In this example, the R in CHORE is an island letter. It’s the only letter in any of the middle four words which doesn’t appear in the words on either side.
        </p>

        <p>
            It may not appear very important in this simple example, but an excess of island letters can make a Morphology puzzle start to feel clunky and arbitrary, because island letters don’t help to move the morph forward. CHOSE could have gone straight to CHOKE without the puzzle losing anything; its absence would make no difference. Whereas if you remove CHOCK, then getting from CHOKE to CHECK requires a larger morph. It’s okay to have an island letter here or there, and sometimes they're necessary for the theme or to make a certain transition, but we should very rarely have multiple words in a row with island letters if it can be avoided.
        </p>

        <SectionHeading level="h3" id="clueing">Word Choice & Clueing</SectionHeading>

        <p>
            Please keep in mind that you are welcome to send in a ladder before clueing it, to check what we think! This can save you considerable time if the word choice was a no-go from the start.
        </p>

        <p>
            With that in mind, it's best to avoid 3-letter words unless they are essential to your theme, and even then. The reason for this is that 3-letter words are generally extremely common terms like EAT, FUN, SEE, SKY, or BEE, and while it isn't impossible to find creative clueing angles on such words, the solutions are typically so obvious as to be rather uninteresting. Consider how rarely a 3-letter entry in a crossword really catches your attention and serves any purpose other than fill. A ladder consisting mostly of 4-letter words would also be suspect unless the clueing was incredibly good. It's best to have most words be no shorter than 5 letters, in order to keep the puzzle from becoming too simplistic.
        </p>

        <p>
            Proper nouns are okay to use so long as they’re still broadly known today. In crosswords it’s often somewhat inevitable to have obscure names in the fill, and you can usually get them by the crosses. The nature of a Morphology puzzle is such that it’s almost never <span className="italicText">necessary</span> to have obscure names, and if a puzzle did have one, it would be much harder (if not impossible) to guess since there’s no crosses to rely on.
        </p>

        <p>
            That said, words and names like BABYLON, SPIDERMAN, PARIS, LINCOLN, LENNON, or SHERLOCK would be perfectly fine, while names like SPITZ, BORZOV, WOTTLE (all 1972 Olympic participants) would be strongly advised against. A name like ELIZABETH would be okay if clued as “Noted Queen” but advised against if clued as “Eckford of the Little Rock Nine.”
        </p>

        <p>
            Clueing should follow standard crossword conventions with regards to singular/plural agreement and tense agreement, as one would expect.
        </p>

        <p>
            Lastly, wordplay and trickery are encouraged. If clueing is overly straightforward – simply a list of definitions, say – it can result in a puzzle that's solved in a minute or two and forgotten just as quickly. Constructors should seek to delight with at least some part of their clueing.
        </p>

        <SectionHeading level="h2" id="themes">Themes</SectionHeading>
        <p>
            Morphology tends to lend itself to themes involving movement, change, transformation, directionality, processes, recipes, steps, sequences, or cycles. DAY becomes NIGHT. STAIRS lead to the ATTIC. WATER and FLOUR become BREAD. From EAST to WEST. And so on.
        </p>

        <p>
            If you have a theme that needs significantly more space than 15 clues allow, perhaps due to the number of themed clues involved, pitch us on it and we will consider buying a double-length puzzle of around 30 clues at $225. However, double-length puzzles are not being published yet - we're considering running them on weekends down the line - and so right now constructors are encouraged to keep their double-length ideas on the backburner. When we start looking for them actively, we'll put out a visible announcement on the main page.
        </p>

        <p>
            Lastly, here is a list of the themes that other contributors have come up with, to avoid using the same idea twice:
            <br/>
            <a target="_blank" href="https://github.com/dailybaffle/morphology-themes">Themes</a>
        </p>
    </div> 
  );
};

export default SubmissionsPage;